import type { Nullable } from '@ricoh/ria.client.core/types/core/data.type';

import { ClientError } from '@ricoh/ria.client.core/errors';
import { omit } from '@ricoh/ria.client.core/utils/data/object';

import type {
  Pattern,
  PatternBatch,
  PatternDocument,
  PatternGenerateGuid,
  PatternGenerateNow,
  PatternGenerateNowType,
  PatternGenerateRandom,
  PatternOutput,
} from '~/common/model/api/batch-type/pattern';

import patternLogic from '~/common/logic/feature/settings/batch-type-library/pattern';

import type { TransformPattern, TransformPatternGlobal } from './_pattern.type';

const NOW_TYPES: PatternGenerateNowType[] = ['datetime', 'date', 'time'];

/**
 * Prepare Pattern.
 *
 * @param {TransformPattern | undefined} pattern Transform Pattern.
 *
 * @returns {Pattern} Pattern.
 */
export function preparePattern(pattern: Nullable<TransformPattern>): Pattern {
  if (!pattern) {
    return {
      $is: 'const',
      type: 'text',
      value: '',
    };
  }

  // Constant
  if ('type' in pattern && !('name' in pattern)) {
    return {
      ...pattern,
      $is: 'const',
    };
  }

  // Output
  if ('type' in pattern && 'name' in pattern && pattern.type === '$parameter' && pattern.name === 'Output') {
    return {
      ...pattern,
      $is: 'output',
    } as PatternOutput;
  }

  // Parameter
  if ('type' in pattern && 'name' in pattern) {
    return {
      ...pattern,
      $is: 'parameter',
    };
  }

  // With Convert
  if (pattern.kind === '$convert') {
    let type = pattern.target.name;
    if (pattern.input.kind === '$now' && !NOW_TYPES.includes(type)) {
      type = 'datetime';
    }

    return {
      $is: pattern.input.kind.substring(1),
      type,
      ...omit(pattern.input, 'kind'),
      transformations: pattern.transformations,
    } as PatternGenerateNow | PatternBatch | PatternDocument;
  }

  switch (pattern.kind) {
    // Generate Now
    case '$now': {
      return {
        $is: 'now',
        type: 'datetime',
        ...omit(pattern, 'kind'),
      };
    }

    // Generate Default
    case '$guid':
    case '$random': {
      return {
        $is: pattern.kind.substring(1),
        ...omit(pattern, 'kind'),
      } as PatternGenerateGuid | PatternGenerateRandom;
    }

    // Global
    case '$global': {
      return {
        $is: 'global',
        ...omit(pattern, 'kind'),
        transformations: pattern.transformations
          ? [
              {
                kind: '$format',
              },
            ]
          : undefined,
      };
    }

    // Batch
    case '$batch': {
      return {
        $is: 'batch',
        ...omit(pattern, 'kind'),
      };
    }

    // Document
    case '$document': {
      return {
        $is: 'document',
        ...omit(pattern, 'kind'),
      };
    }

    default: {
      throw new ClientError({
        module: 'service',
        message: `Invalid pattern`,
        cause: pattern,
      });
    }
  }
}

/**
 * Convert Pattern.
 *
 * @param {PatternGenerateNow | PatternBatch | PatternDocument} pattern Pattern.
 *
 * @returns {TransformPattern} Converted Pattern.
 */
function convertPattern(pattern: PatternGenerateNow | PatternBatch | PatternDocument): TransformPattern {
  if (pattern.$is !== 'now' && !pattern.type) {
    return {
      kind: `$${pattern.$is}`,
      ...omit(pattern, '$is', 'type'),
    };
  }

  return {
    kind: '$convert',
    input: {
      kind: `$${pattern.$is}`,
      ...omit(pattern, '$is', 'type', 'transformations'),
    },
    target: {
      name: pattern.type,
    },
    transformations: pattern.transformations,
  } as TransformPattern;
}

/**
 * Transform Pattern.
 *
 * @param {Pattern} pattern Pattern.
 * @param {string} format Format.
 *
 * @returns {TransformPattern} Transformed Pattern.
 */
export function transformPattern(pattern: Pattern, format?: boolean): TransformPattern {
  switch (pattern.$is) {
    // Constant
    case 'const': {
      return omit(pattern, '$is') as TransformPattern;
    }

    // Parameter
    case 'parameter': {
      return {
        type: '$parameter',
        ...omit(pattern, '$is'),
      };
    }

    // Output
    case 'output': {
      return {
        type: `$parameter`,
        ...omit(pattern, '$is'),
      };
    }

    // Generate
    case 'now': {
      return convertPattern(pattern);
    }
    case 'guid':
    case 'random': {
      return {
        kind: `$${pattern.$is}`,
        ...omit(pattern, '$is'),
      };
    }

    // Global
    case 'global': {
      if (format) {
        return {
          kind: '$global',
          ...omit(pattern, '$is'),
          transformations: [{ kind: '$format' }],
        };
      }

      return {
        kind: '$global',
        ...omit(pattern, '$is'),
        transformations:
          pattern.path && pattern.transformations
            ? [
                {
                  kind: '$set',
                  target: {
                    type: '$variable',
                    annotation: '$global',
                    name: patternLogic.global.patternValue.globalVariable.fromPath(pattern.path),
                  },
                },
              ]
            : undefined,
      } as TransformPatternGlobal;
    }

    // Batch & Document
    case 'batch':
    case 'document': {
      return convertPattern(pattern);
    }

    default: {
      throw new ClientError({
        module: 'service',
        message: `Invalid pattern`,
        cause: pattern,
      });
    }
  }
}
